import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import PricingPlans from "../components/PricingPlans";
import SeoHead from "../components/seo/SeoHead";
import API from "../services/api";
import useI18n from "../i18n/useI18n";

const THEME_KEY = "hookora_theme";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₦${amount.toLocaleString()}`;
};

function PricingPage() {
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [billing, setBilling] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [walletLedger, setWalletLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [walletFundingLoading, setWalletFundingLoading] = useState(false);
  const [walletAmount, setWalletAmount] = useState("5000");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      dangerButton: "#dc2626",
      statusPillBg: isDark ? "rgba(30,41,59,0.85)" : "#f8fafc",
      statusPillBorder: isDark ? "rgba(148,163,184,0.20)" : "#e5e7eb",
      statusPillText: isDark ? "#e2e8f0" : "#111827",
      infoBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      rewardsCardBg: isDark ? "rgba(15,23,42,0.76)" : "#ffffff",
      rewardsAccentBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      rewardsAccentBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      rewardsAccentText: isDark ? "#bfdbfe" : "#1d4ed8",
      walletBg: isDark ? "rgba(88,28,135,0.14)" : "#faf5ff",
      walletBorder: isDark ? "rgba(192,132,252,0.2)" : "#e9d5ff",
      walletText: isDark ? "#e9d5ff" : "#7c3aed",
      creatorBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      creatorBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      creatorText: isDark ? "#bfdbfe" : "#1d4ed8",
      proBg: isDark ? "rgba(109,40,217,0.14)" : "#f5f3ff",
      proBorder: isDark ? "rgba(216,180,254,0.22)" : "#d8b4fe",
      proText: isDark ? "#e9d5ff" : "#7c3aed",
      freeBg: isDark ? "rgba(120,53,15,0.14)" : "#fff7ed",
      freeBorder: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
      freeText: isDark ? "#fde68a" : "#9a3412",
      positiveText: isDark ? "#86efac" : "#166534",
      negativeText: isDark ? "#fca5a5" : "#b91c1c",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    background: colors.inputBg,
    color: colors.inputText,
    boxSizing: "border-box",
    outline: "none",
    fontSize: "14px",
  };

  const formatLedgerType = (value) => {
    const map = {
      funding: tt("pricing.walletFunding", "Wallet funding"),
      billing_applied: tt("pricing.appliedToBilling", "Applied to billing"),
    };
    return map[value] || value?.replaceAll("_", " ") || tt("pricing.transaction", "Transaction");
  };

  const formatLedgerAmount = (value) => {
    const amount = Number(value || 0);
    const sign = amount > 0 ? "+" : "";
    return `${sign}${formatCurrency(amount)}`;
  };

  const loadRewardsSummary = async () => {
    try {
      const response = await API.get("/referrals/me");
      setRewards(response.data);
    } catch (err) {
      console.error("Failed to load rewards summary:", err);
      setRewards(null);
    }
  };

  const loadWalletLedger = async () => {
    try {
      const response = await API.get("/billing/wallet/ledger");
      setWalletLedger(response.data || []);
    } catch (err) {
      console.error("Failed to load wallet ledger:", err);
      setWalletLedger([]);
    }
  };

  const loadBillingStatus = async () => {
    try {
      setLoading(true);
      const response = await API.get("/billing/status");
      setBilling(response.data);
      setError("");
      await Promise.all([loadRewardsSummary(), loadWalletLedger()]);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          tt("pricing.messages.couldNotLoadBillingDetails", "Could not load billing details.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBillingStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      tt(
        "pricing.messages.confirmCancelSubscription",
        "Are you sure you want to cancel your subscription?"
      )
    );

    if (!confirmed) return;

    try {
      setCancelLoading(true);
      setMessage("");
      setError("");

      const response = await API.post("/billing/cancel-subscription");
      setMessage(
        response.data.message ||
          tt(
            "pricing.messages.subscriptionCancelledSuccessfully",
            "Subscription cancelled successfully."
          )
      );
      await loadBillingStatus();
      window.dispatchEvent(new CustomEvent("hookora-auth-updated"));
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          tt(
            "pricing.messages.couldNotCancelSubscription",
            "Could not cancel subscription."
          )
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleWalletFunding = async () => {
    const amount = Number(walletAmount || 0);

    if (!amount || amount < 100) {
      setError(tt("pricing.messages.invalidWalletFundingAmount", "Enter a valid wallet funding amount."));
      setMessage("");
      return;
    }

    try {
      setWalletFundingLoading(true);
      setMessage("");
      setError("");

      const response = await API.post("/billing/wallet/fund", {
        amount_naira: amount,
      });

      const checkoutUrl = response?.data?.checkout_url;
      if (!checkoutUrl) {
        throw new Error(
          tt(
            "pricing.messages.walletFundingUrlNotReturned",
            "Wallet funding URL was not returned."
          )
        );
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          tt(
            "pricing.messages.couldNotStartWalletFunding",
            "Could not start wallet funding."
          )
      );
    } finally {
      setWalletFundingLoading(false);
    }
  };

  const isPaidPlan = billing?.plan === "creator" || billing?.plan === "pro";

  const formatPlanName = (plan) => {
    if (!plan) return tt("plans.free", "Free");
    if (plan === "creator") return tt("plans.creator", "Creator");
    if (plan === "pro") return tt("plans.pro", "Pro");
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const formatBillingStatus = (status) => {
    if (!status) return tt("pricing.inactive", "Inactive");
    return status
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatBillingInterval = (interval) => {
    if (!interval) return tt("pricing.monthly", "Monthly");
    return interval === "yearly"
      ? tt("pricing.yearly", "Yearly")
      : tt("pricing.monthly", "Monthly");
  };

  const planTierCard =
    billing?.plan === "pro"
      ? {
          background: colors.proBg,
          border: colors.proBorder,
          color: colors.proText,
          title: tt("pricing.proPlanAccess", "Pro plan access"),
          text: tt(
            "pricing.proPlanAccessDescription",
            "You currently have access to Hookora’s advanced strategic tools."
          ),
        }
      : billing?.plan === "creator"
      ? {
          background: colors.creatorBg,
          border: colors.creatorBorder,
          color: colors.creatorText,
          title: tt("pricing.creatorPlanAccess", "Creator plan access"),
          text: tt(
            "pricing.creatorPlanAccessDescription",
            "You currently have access to Hookora’s premium workflow tools."
          ),
        }
      : {
          background: colors.freeBg,
          border: colors.freeBorder,
          color: colors.freeText,
          title: tt("pricing.freePlanAccess", "Free plan access"),
          text: tt(
            "pricing.freePlanAccessDescription",
            "You are currently using Hookora’s core free tools."
          ),
        };

  const planUnlockSummary = [
    {
      title: tt("plans.free", "Free"),
      text: tt("pricing.planUnlockFreeText", "Core writing tools for everyday content creation."),
      background: colors.freeBg,
      border: colors.freeBorder,
      color: colors.freeText,
    },
    {
      title: tt("plans.creator", "Creator"),
      text: tt("pricing.planUnlockCreatorText", "Premium workflow tools for stronger packaging, rewrites, and retention."),
      background: colors.creatorBg,
      border: colors.creatorBorder,
      color: colors.creatorText,
    },
    {
      title: tt("plans.pro", "Pro"),
      text: tt("pricing.planUnlockProText", "Advanced strategic tools for deeper creator systems and differentiation."),
      background: colors.proBg,
      border: colors.proBorder,
      color: colors.proText,
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
      }}
    >
      <SeoHead
        title="Billing | Hookora"
        description={tt(
          "pricing.seoDescription",
          "Manage your Hookora billing, wallet balance, reward credits, subscriptions, and plan status."
        )}
        path="/app/pricing"
      />
      <Navbar />

      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "24px 16px 0",
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "18px",
            padding: "22px",
            boxShadow: colors.shadow,
            marginBottom: "18px",
            backdropFilter: isDark ? "blur(14px)" : "none",
          }}
        >
          <div style={{ marginBottom: "18px" }}>
            <h2 style={{ margin: "0 0 8px", color: colors.text }}>
              {tt("pricing.billing", "Billing")}
            </h2>
            <p style={{ margin: 0, color: colors.textMuted, lineHeight: "1.7" }}>
              {tt(
                "pricing.billingPageIntro",
                "Review your current plan, billing status, billing interval, Reward Credits, Wallet Balance, and subscription controls."
              )}
            </p>
          </div>

          {loading ? (
            <p style={{ color: colors.textMuted }}>{tt("common.loading", "Loading")}...</p>
          ) : (
            <>
              <div
                style={{
                  background: planTierCard.background,
                  border: `1px solid ${planTierCard.border}`,
                  borderRadius: "16px",
                  padding: "16px",
                  marginBottom: "18px",
                }}
              >
                <div
                  style={{
                    color: planTierCard.color,
                    fontWeight: 800,
                    fontSize: "16px",
                    marginBottom: "8px",
                  }}
                >
                  {planTierCard.title}
                </div>

                <div
                  style={{
                    color: planTierCard.color,
                    fontSize: "14px",
                    lineHeight: "1.7",
                  }}
                >
                  {planTierCard.text}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "12px",
                  marginBottom: "18px",
                }}
              >
                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                    {tt("pricing.currentPlan", "Current plan")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px" }}>
                    {formatPlanName(billing?.plan)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                    {tt("pricing.billingStatus", "Billing status")}
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      background: colors.statusPillBg,
                      border: `1px solid ${colors.statusPillBorder}`,
                      color: colors.statusPillText,
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    {formatBillingStatus(billing?.billing_status)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                    {tt("pricing.billingInterval", "Billing interval")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px" }}>
                    {formatBillingInterval(billing?.billing_interval)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.rewardsAccentBg,
                    border: `1px solid ${colors.rewardsAccentBorder}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                    {tt("pricing.rewardCredits", "Reward Credits")}
                  </div>
                  <div
                    style={{
                      color: colors.rewardsAccentText,
                      fontWeight: 800,
                      fontSize: "20px",
                    }}
                  >
                    {formatCurrency(
                      rewards?.reward_credits_balance_naira ||
                        billing?.reward_credits_balance_naira ||
                        0
                    )}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.walletBg,
                    border: `1px solid ${colors.walletBorder}`,
                    borderRadius: "14px",
                    padding: "14px",
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                    {tt("pricing.walletBalance", "Wallet Balance")}
                  </div>
                  <div
                    style={{
                      color: colors.walletText,
                      fontWeight: 800,
                      fontSize: "20px",
                    }}
                  >
                    {formatCurrency(billing?.wallet_balance_naira || 0)}
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: colors.rewardsCardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "14px",
                  padding: "16px",
                  marginBottom: "18px",
                }}
              >
                <h3 style={{ margin: "0 0 10px", color: colors.text, fontSize: "18px" }}>
                  {tt("pricing.creatorRewards", "Creator Rewards")} + {tt("pricing.walletBalance", "Wallet Balance")}
                </h3>
                <p
                  style={{
                    margin: "0 0 10px",
                    color: colors.textMuted,
                    lineHeight: "1.7",
                    fontSize: "14px",
                  }}
                >
                  {tt(
                    "pricing.rewardsAndWalletDescription",
                    "Reward Credits and Wallet Balance help reduce how much you pay by card during checkout."
                  )}
                </p>
                <p
                  style={{
                    margin: 0,
                    color: colors.textSoft,
                    lineHeight: "1.7",
                    fontSize: "14px",
                  }}
                >
                  {tt("pricing.checkoutOrderPrefix", "At checkout, Hookora applies")}{" "}
                  <strong>{tt("pricing.rewardCredits", "Reward Credits")}</strong>{" "}
                  {tt("pricing.then", "then")} <strong>{tt("pricing.walletBalance", "Wallet Balance")}</strong>{" "}
                  {tt("pricing.then", "then")} <strong>{tt("pricing.cardPayment", "Card Payment")}</strong>{" "}
                  {tt("pricing.checkoutOrderSuffix", "to cover the remaining amount.")}
                </p>
              </div>

              <div
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "14px",
                  padding: "16px",
                  marginBottom: "18px",
                }}
              >
                <h3 style={{ margin: "0 0 12px", color: colors.text, fontSize: "18px" }}>
                  {tt("pricing.whatEachPlanUnlocks", "What each plan unlocks")}
                </h3>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {planUnlockSummary.map((item) => (
                    <div
                      key={item.title}
                      style={{
                        background: item.background,
                        border: `1px solid ${item.border}`,
                        borderRadius: "14px",
                        padding: "14px",
                      }}
                    >
                      <div
                        style={{
                          color: item.color,
                          fontWeight: 800,
                          fontSize: "15px",
                          marginBottom: "6px",
                        }}
                      >
                        {item.title}
                      </div>

                      <div
                        style={{
                          color: item.color,
                          fontSize: "13px",
                          lineHeight: "1.7",
                        }}
                      >
                        {item.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "14px",
                  marginBottom: "18px",
                }}
              >
                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "16px",
                  }}
                >
                  <h3 style={{ margin: "0 0 10px", color: colors.text, fontSize: "17px" }}>
                    {tt("pricing.fundWalletBalance", "Fund Wallet Balance")}
                  </h3>

                  <div
                    style={{
                      color: colors.textMuted,
                      fontSize: "14px",
                      lineHeight: "1.7",
                      marginBottom: "12px",
                    }}
                  >
                    {tt(
                      "pricing.fundWalletBalanceDescription",
                      "Add money to your Wallet Balance so future billing can use it before charging your card."
                    )}
                  </div>

                  <input
                    type="number"
                    min="100"
                    step="100"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(e.target.value)}
                    placeholder={tt("pricing.enterAmount", "Enter amount")}
                    style={inputStyle}
                  />

                  <button
                    onClick={handleWalletFunding}
                    disabled={walletFundingLoading}
                    style={{
                      marginTop: "12px",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: "none",
                      background: "#7c3aed",
                      color: "#ffffff",
                      fontWeight: "700",
                      cursor: walletFundingLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {walletFundingLoading
                      ? tt("pricing.redirecting", "Redirecting...")
                      : tt("pricing.fundWallet", "Fund Wallet")}
                  </button>
                </div>

                <div
                  style={{
                    background: colors.infoBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "16px",
                  }}
                >
                  <h3 style={{ margin: "0 0 10px", color: colors.text, fontSize: "17px" }}>
                    {tt("pricing.walletTransactionHistory", "Wallet transaction history")}
                  </h3>

                  {walletLedger.length ? (
                    <div style={{ display: "grid", gap: "10px", maxHeight: "240px", overflowY: "auto" }}>
                      {walletLedger.slice(0, 8).map((entry) => (
                        <div
                          key={entry.id}
                          style={{
                            background: colors.cardBg,
                            border: `1px solid ${colors.border}`,
                            borderRadius: "12px",
                            padding: "12px 14px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "10px",
                              flexWrap: "wrap",
                              marginBottom: "4px",
                            }}
                          >
                            <div style={{ color: colors.text, fontWeight: "700", fontSize: "14px" }}>
                              {formatLedgerType(entry.entry_type)}
                            </div>
                            <div
                              style={{
                                color:
                                  Number(entry.amount_naira || 0) >= 0
                                    ? colors.positiveText
                                    : colors.negativeText,
                                fontWeight: "800",
                                fontSize: "14px",
                              }}
                            >
                              {formatLedgerAmount(entry.amount_naira)}
                            </div>
                          </div>
                          <div style={{ color: colors.textMuted, fontSize: "12px", lineHeight: "1.6" }}>
                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : "—"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: colors.textMuted, fontSize: "14px", lineHeight: "1.7" }}>
                      {tt("pricing.noWalletTransactions", "No wallet transactions yet.")}
                    </div>
                  )}
                </div>
              </div>

              {isPaidPlan && billing?.paystack_subscription_code ? (
                <div
                  style={{
                    borderTop: `1px solid ${colors.border}`,
                    paddingTop: "18px",
                  }}
                >
                  <h3 style={{ margin: "0 0 8px", color: colors.text, fontSize: "18px" }}>
                    {tt("pricing.cancelSubscription", "Cancel subscription")}
                  </h3>

                  <p
                    style={{
                      margin: "0 0 14px",
                      color: colors.textMuted,
                      lineHeight: "1.7",
                      fontSize: "14px",
                    }}
                  >
                    {tt(
                      "pricing.cancelSubscriptionDescription",
                      "You can cancel your current paid subscription here. Your access remains active until the current billing period ends."
                    )}
                  </p>

                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    style={{
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: "none",
                      background: cancelLoading ? "#9ca3af" : colors.dangerButton,
                      color: "#ffffff",
                      fontWeight: "700",
                      cursor: cancelLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {cancelLoading
                      ? tt("pricing.cancelling", "Cancelling...")
                      : tt("pricing.cancelSubscription", "Cancel subscription")}
                  </button>

                  <p
                    style={{
                      marginTop: "12px",
                      color: colors.textMuted,
                      lineHeight: "1.7",
                      fontSize: "14px",
                    }}
                  >
                    {tt(
                      "pricing.cancellationFooter",
                      "After cancellation, your plan will remain active until the end of the current billing period."
                    )}
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    borderTop: `1px solid ${colors.border}`,
                    paddingTop: "18px",
                  }}
                >
                  <h3 style={{ margin: "0 0 8px", color: colors.text, fontSize: "18px" }}>
                    {tt("pricing.subscriptionStatus", "Subscription status")}
                  </h3>
                  <p style={{ margin: 0, color: colors.textMuted, lineHeight: "1.7" }}>
                    {tt(
                      "pricing.subscriptionStatusDescription",
                      "You do not currently have an active recurring paid subscription to manage here."
                    )}
                  </p>
                </div>
              )}

              {message && (
                <div
                  style={{
                    marginTop: "14px",
                    background: colors.successBg,
                    border: `1px solid ${colors.successBorder}`,
                    color: colors.successText,
                    padding: "12px",
                    borderRadius: "12px",
                    lineHeight: "1.6",
                  }}
                >
                  {message}
                </div>
              )}

              {error && (
                <div
                  style={{
                    marginTop: "14px",
                    background: colors.errorBg,
                    border: `1px solid ${colors.errorBorder}`,
                    color: colors.errorText,
                    padding: "12px",
                    borderRadius: "12px",
                    lineHeight: "1.6",
                  }}
                >
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PricingPlans heading={tt("pricing.heading", "Plans built for creators")} />
    </div>
  );
}

export default PricingPage;